import { createApi } from "@reduxjs/toolkit/query/react";

import {
  baseQuery,
  GET_SUPPLIER_PORTAL,
  GET_SUPPLIER_PORTAL_QUESTIONS,
  GET_SUPPLIER_PORTAL_DOC_SLOTS,
  ANSWER_SUPPLIER_PORTAL_QUESTION,
  UPLOAD_SUPPLIER_PORTAL_DOCUMENT,
  DELETE_SUPPLIER_PORTAL_DOCUMENT,
  GENERATE_FIRST_SALE_PORTAL_TOKEN,
  REVOKE_FIRST_SALE_PORTAL_TOKEN,
  UPDATE_PORTAL_DOC_SLOT_NOTE,
  GET_SUPPLIER_PORTAL_DOCUMENT_REQUESTS,
  CONFIRM_SUPPLIER_PORTAL_DOCUMENT_UPLOAD,
  SEND_PORTAL_VERIFICATION_CODE,
  VERIFY_PORTAL_CODE,
  TOGGLE_PORTAL_2FA,
  ADD_PORTAL_AUTHORIZED_EMAIL,
  REMOVE_PORTAL_AUTHORIZED_EMAIL,
} from "@/graphql";

export const firstSalePortalApi = createApi({
  reducerPath: "api/firstSalePortal",
  tagTypes: [
    "PortalCaseInfo",
    "PortalQuestions",
    "PortalDocSlots",
    "PortalDocRequests",
  ],
  baseQuery,
  endpoints: (builder) => ({
    // ─── Queries ──────────────────────────────────────────

    getSupplierPortal: builder.query<
      any,
      { token: string; sessionToken?: string }
    >({
      query: (variables) => ({
        document: GET_SUPPLIER_PORTAL,
        variables,
      }),
      transformResponse: (response: any) => response.supplierPortal,
      providesTags: ["PortalCaseInfo"],
    }),

    getSupplierPortalQuestions: builder.query<
      any[],
      { token: string; sessionToken?: string }
    >({
      query: (variables) => ({
        document: GET_SUPPLIER_PORTAL_QUESTIONS,
        variables,
      }),
      transformResponse: (response: any) => response.supplierPortalQuestions,
      providesTags: ["PortalQuestions"],
    }),

    getSupplierPortalDocSlots: builder.query<
      any[],
      { token: string; sessionToken?: string }
    >({
      query: (variables) => ({
        document: GET_SUPPLIER_PORTAL_DOC_SLOTS,
        variables,
      }),
      transformResponse: (response: any) => response.supplierPortalDocSlots,
      providesTags: ["PortalDocSlots"],
    }),

    // ─── Mutations ────────────────────────────────────────

    answerSupplierPortalQuestion: builder.mutation<
      any,
      { token: string; sessionToken?: string; questionId: string; answer: string }
    >({
      query: (variables) => ({
        document: ANSWER_SUPPLIER_PORTAL_QUESTION,
        variables,
      }),
      transformResponse: (response: any) => response.answerSupplierPortalQuestion,
      invalidatesTags: ["PortalQuestions", "PortalDocSlots"],
    }),

    uploadSupplierPortalDocument: builder.mutation<
      any,
      { token: string; sessionToken?: string; slotId: string; filename: string; mimeType: string; sizeBytes: number }
    >({
      query: (variables) => ({
        document: UPLOAD_SUPPLIER_PORTAL_DOCUMENT,
        variables,
      }),
      transformResponse: (response: any) => response.uploadSupplierPortalDocument,
      invalidatesTags: ["PortalDocSlots"],
    }),

    deleteSupplierPortalDocument: builder.mutation<
      boolean,
      { token: string; sessionToken?: string; docId: string }
    >({
      query: (variables) => ({
        document: DELETE_SUPPLIER_PORTAL_DOCUMENT,
        variables,
      }),
      transformResponse: (response: any) => response.deleteSupplierPortalDocument,
      invalidatesTags: ["PortalDocSlots"],
    }),

    generateFirstSalePortalToken: builder.mutation<
      { token: string },
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GENERATE_FIRST_SALE_PORTAL_TOKEN,
        variables,
      }),
      transformResponse: (response: any) => response.generateFirstSalePortalToken,
      invalidatesTags: ["PortalCaseInfo"],
    }),

    revokeFirstSalePortalToken: builder.mutation<
      boolean,
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: REVOKE_FIRST_SALE_PORTAL_TOKEN,
        variables,
      }),
      transformResponse: (response: any) => response.revokeFirstSalePortalToken,
      invalidatesTags: ["PortalCaseInfo"],
    }),

    updatePortalDocSlotNote: builder.mutation<
      boolean,
      { caseId: string; slotId: string; tenantId?: string; importerNote?: string; reviewFeedback?: string }
    >({
      query: (variables) => ({
        document: UPDATE_PORTAL_DOC_SLOT_NOTE,
        variables,
      }),
      transformResponse: (response: any) => response.updatePortalDocSlotNote,
      invalidatesTags: ["PortalDocSlots"],
    }),

    confirmSupplierPortalDocumentUpload: builder.mutation<
      any,
      { token: string; sessionToken?: string; docId: string }
    >({
      query: (variables) => ({
        document: CONFIRM_SUPPLIER_PORTAL_DOCUMENT_UPLOAD,
        variables,
      }),
      transformResponse: (response: any) => response.confirmSupplierPortalDocumentUpload,
      invalidatesTags: ["PortalDocSlots"],
    }),

    getSupplierPortalDocumentRequests: builder.query<
      any[],
      { token: string; sessionToken?: string }
    >({
      query: (variables) => ({
        document: GET_SUPPLIER_PORTAL_DOCUMENT_REQUESTS,
        variables,
      }),
      transformResponse: (response: any) => response.supplierPortalDocumentRequests,
      providesTags: ["PortalDocRequests"],
    }),

    // ─── Portal 2FA ──────────────────────────────────────

    sendPortalVerificationCode: builder.mutation<
      { success: boolean },
      { token: string; email: string }
    >({
      query: (variables) => ({
        document: SEND_PORTAL_VERIFICATION_CODE,
        variables,
      }),
      transformResponse: (response: any) => response.sendPortalVerificationCode,
    }),

    verifyPortalCode: builder.mutation<
      { verified: boolean; sessionToken?: string },
      { token: string; email: string; code: string }
    >({
      query: (variables) => ({
        document: VERIFY_PORTAL_CODE,
        variables,
      }),
      transformResponse: (response: any) => response.verifyPortalCode,
    }),

    togglePortal2fa: builder.mutation<
      boolean,
      { caseId: string; tenantId?: string; enabled: boolean }
    >({
      query: (variables) => ({
        document: TOGGLE_PORTAL_2FA,
        variables,
      }),
      transformResponse: (response: any) => response.togglePortal2fa,
      invalidatesTags: ["PortalCaseInfo"],
    }),

    addPortalAuthorizedEmail: builder.mutation<
      string[],
      { caseId: string; tenantId?: string; email: string }
    >({
      query: (variables) => ({
        document: ADD_PORTAL_AUTHORIZED_EMAIL,
        variables,
      }),
      transformResponse: (response: any) => response.addPortalAuthorizedEmail,
    }),

    removePortalAuthorizedEmail: builder.mutation<
      string[],
      { caseId: string; tenantId?: string; email: string }
    >({
      query: (variables) => ({
        document: REMOVE_PORTAL_AUTHORIZED_EMAIL,
        variables,
      }),
      transformResponse: (response: any) => response.removePortalAuthorizedEmail,
    }),
  }),
});

export const {
  useGetSupplierPortalQuery,
  useGetSupplierPortalQuestionsQuery,
  useGetSupplierPortalDocSlotsQuery,
  useAnswerSupplierPortalQuestionMutation,
  useUploadSupplierPortalDocumentMutation,
  useDeleteSupplierPortalDocumentMutation,
  useGenerateFirstSalePortalTokenMutation,
  useRevokeFirstSalePortalTokenMutation,
  useUpdatePortalDocSlotNoteMutation,
  useConfirmSupplierPortalDocumentUploadMutation,
  useGetSupplierPortalDocumentRequestsQuery,
  useSendPortalVerificationCodeMutation,
  useVerifyPortalCodeMutation,
  useTogglePortal2faMutation,
  useAddPortalAuthorizedEmailMutation,
  useRemovePortalAuthorizedEmailMutation,
} = firstSalePortalApi;
